import { useState } from "react";
import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";
import { IlloStyles, ContactIllo } from "../components/AnimatedIllustrations";

const GlowOrb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);

const CONTACT_REASONS = [
  { label: "New Project", icon: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5"><path d="M12 4.5v15m7.5-7.5h-15" /></svg> },
  { label: "Technical Consultation", icon: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5"><path d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0zm-9 5.25h.008v.008H12v-.008z" /></svg> },
  { label: "Partnership", icon: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5"><path d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0z" /></svg> },
  { label: "General Inquiry", icon: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5"><path d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg> },
];

const PROCESS_STEPS = [
  { step: "01", title: "We respond within 4–8 hours", desc: "Our team reviews every message personally and gets back to you the same business day." },
  { step: "02", title: "Free 30-min discovery call", desc: "We'll discuss your project goals, timeline, and budget — no sales pressure, just honest conversation." },
  { step: "03", title: "Detailed proposal", desc: "We send a clear scope, timeline, and fixed-price quote within 48 hours of our call." },
  { step: "04", title: "Project kickoff", desc: "Once you're happy, we onboard your project within one week and start shipping." },
];

export default function ContactPage() {
  useSEO({
    title: "Contact BerryByte | Free Software Consultation",
    description: "Get in touch with BerryByte Innovations. Start your custom software project or book a free consultation. We respond within 4-8 business hours.",
    keywords: "contact software development agency, hire software developers US Canada, custom software project inquiry, free software consultation, BerryByte contact",
    canonical: "https://www.berrybyteinn.com/contact",
  });

  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedReason, setSelectedReason] = useState("New Project");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch("https://api.berrybyteinn.com/api/contact-us", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: form.name,
          email: form.email,
          phone_number: form.phone,
          subject: selectedReason + (form.subject ? ` — ${form.subject}` : ""),
          message: form.message,
          platform: "berry",
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      setSent(true);
    } catch {
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SharedLayout>
      {/* Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <GlowOrb className="w-[700px] h-[700px] bg-violet-600 -top-40 -left-40" />
        <GlowOrb className="w-[500px] h-[500px] bg-cyan-600 -top-20 -right-32" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        {/* decorative animated illustration */}
        <div aria-hidden="true" className="hidden 2xl:block absolute right-12 top-1/2 -translate-y-1/2 w-[300px] pointer-events-none select-none opacity-80">
          <IlloStyles />
          <ContactIllo />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-sm font-medium mb-6 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Currently accepting new projects · Response within 4–8 hrs
          </div>
          <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight mb-6">
            Let's build something
            <br />
            <span className="bg-gradient-to-r from-violet-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              great together.
            </span>
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Tell us about your project. Our team will review your message and get back to you within one business day — no automated replies.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative py-16 bg-[#050810] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">

            {/* Left: Info Panel */}
            <div className="lg:col-span-2 space-y-8">
              {/* Contact Details */}
              <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                <h2 className="text-lg font-bold text-white mb-5">Contact Information</h2>
                <div className="space-y-4">
                  {[
                    {
                      icon: () => (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
                          <path d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                        </svg>
                      ),
                      label: "Email",
                      value: "sales@berrybyteinn.com",
                      href: "mailto:sales@berrybyteinn.com",
                    },
                    {
                      icon: () => (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
                          <path d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25z" />
                        </svg>
                      ),
                      label: "Phone",
                      value: "+1 201-846-9830",
                      href: "tel:+12018469830",
                    },
                    {
                      icon: () => (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
                          <path d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                          <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0z" />
                        </svg>
                      ),
                      label: "Locations",
                      values: ["Texas, United States", "Karachi, Pakistan"],
                    },
                  ].map(({ icon: Icon, label, value, href, values }) => (
                    <div key={label} className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center text-violet-400 flex-shrink-0">
                        <Icon />
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 font-medium">{label}</div>
                        {values ? (
                          <div className="flex flex-col gap-0.5">
                            {values.map((v) => (
                              <div key={v} className="text-slate-200 text-sm font-medium">{v}</div>
                            ))}
                          </div>
                        ) : href ? (
                          <a href={href} className="text-slate-200 text-sm font-medium hover:text-white transition-colors">
                            {value}
                          </a>
                        ) : (
                          <div className="text-slate-200 text-sm font-medium">{value}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status */}
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-emerald-400 text-sm font-semibold">Currently accepting new projects</span>
                </div>
                <p className="text-slate-400 text-sm">Typical response time: 4–8 business hours. We review every inquiry personally.</p>
              </div>

              {/* What happens next */}
              <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                <h3 className="text-white font-bold mb-5">What happens next?</h3>
                <div className="space-y-4">
                  {PROCESS_STEPS.map((step) => (
                    <div key={step.step} className="flex gap-4">
                      <div className="w-7 h-7 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-400 text-xs font-bold flex-shrink-0 mt-0.5">
                        {step.step}
                      </div>
                      <div>
                        <div className="text-slate-200 text-sm font-semibold">{step.title}</div>
                        <div className="text-slate-500 text-xs mt-0.5 leading-relaxed">{step.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <div className="rounded-3xl border border-white/8 bg-white/[0.02] p-8 backdrop-blur-sm">
                {sent ? (
                  <div className="flex flex-col items-center justify-center text-center py-20">
                    <div className="w-20 h-20 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mb-6">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-10 h-10 text-emerald-400">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-3xl font-black text-white mb-3">Message Sent!</h3>
                    <p className="text-slate-400 text-lg max-w-sm">
                      Thanks for reaching out. We'll review your message and get back to you within 4–8 business hours.
                    </p>
                    <button
                      onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", subject: "", message: "" }); setError(""); setSelectedReason("New Project"); }}
                      className="mt-8 px-6 py-3 rounded-xl border border-white/10 text-slate-300 hover:text-white hover:bg-white/5 text-sm font-medium transition-all"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                    <div>
                      <h2 className="text-2xl font-black text-white mb-1">Send us a message</h2>
                      <p className="text-slate-500 text-sm">Fill in the details below and we'll get back to you promptly.</p>
                    </div>

                    {/* Reason selector */}
                    <div>
                      <label className="text-xs font-medium text-slate-400 block mb-3">What can we help you with?</label>
                      <div className="grid grid-cols-2 gap-2">
                        {CONTACT_REASONS.map(({ label, icon: Icon }) => (
                          <button
                            key={label}
                            type="button"
                            onClick={() => setSelectedReason(label)}
                            className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-200 ${
                              selectedReason === label
                                ? "border-violet-500/60 bg-violet-500/15 text-violet-300"
                                : "border-white/8 bg-white/[0.02] text-slate-400 hover:text-slate-200 hover:bg-white/[0.05] hover:border-white/15"
                            }`}
                          >
                            <span className={selectedReason === label ? "text-violet-400" : "text-slate-500"}>
                              <Icon />
                            </span>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Name + Email */}
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-xs font-medium text-slate-400 block mb-2">Full Name <span className="text-rose-400">*</span></label>
                        <input
                          required
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Jane Doe"
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/8 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-violet-500/50 focus:bg-white/8 transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-400 block mb-2">Email Address <span className="text-rose-400">*</span></label>
                        <input
                          required
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="jane@company.com"
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/8 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-violet-500/50 focus:bg-white/8 transition-all duration-200"
                        />
                      </div>
                    </div>

                    {/* Phone + Subject */}
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-xs font-medium text-slate-400 block mb-2">Phone Number</label>
                        <input
                          type="tel"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          placeholder="+1 201-846-9830"
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/8 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-violet-500/50 focus:bg-white/8 transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-400 block mb-2">Project / Company Name</label>
                        <input
                          value={form.subject}
                          onChange={(e) => setForm({ ...form, subject: e.target.value })}
                          placeholder="My Startup, Inc."
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/8 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-violet-500/50 focus:bg-white/8 transition-all duration-200"
                        />
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="text-xs font-medium text-slate-400 block mb-2">Tell us about your project <span className="text-rose-400">*</span></label>
                      <textarea
                        required
                        rows={6}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="Describe your project, goals, timeline, and budget range. The more detail, the better we can help you..."
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/8 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-violet-500/50 focus:bg-white/8 transition-all duration-200 resize-none"
                      />
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-rose-400 flex-shrink-0">
                          <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        </svg>
                        <p className="text-rose-400 text-sm">{error}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 rounded-xl bg-gradient-to-r from-violet-600 to-cyan-600 text-white font-semibold text-base hover:from-violet-500 hover:to-cyan-500 hover:-translate-y-0.5 shadow-lg shadow-violet-900/30 hover:shadow-violet-900/50 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Sending your message...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          Send Message
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </span>
                      )}
                    </button>

                    <p className="text-slate-600 text-xs text-center">
                      By submitting, you agree to receive a response from our team. We never share your data with third parties.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom strip */}
      <section className="relative py-20 bg-[#070b14] border-t border-white/5">
        <GlowOrb className="w-[500px] h-[500px] bg-violet-700 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
            Prefer to talk directly?
          </h2>
          <p className="text-slate-400 mb-8">
            Give us a call or send an email — whichever works best for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+12018469830"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-violet-600 to-cyan-600 text-white font-semibold hover:from-violet-500 hover:to-cyan-500 hover:-translate-y-1 transition-all duration-300 shadow-2xl shadow-violet-900/40"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
                <path d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25z" />
              </svg>
              +1 201-846-9830
            </a>
            <a
              href="mailto:sales@berrybyteinn.com"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl border border-white/10 text-white font-semibold bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
                <path d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
              </svg>
              sales@berrybyteinn.com
            </a>
          </div>
        </div>
      </section>
    </SharedLayout>
  );
}
